import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormContext, useFormState, Controller, useFieldArray } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import BrandManageModal from '@features/BrandManageModal';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import FieldWithActions, { rowActions } from '@components/FieldWithActions';
import { useTranslation, usePermissions } from '@hooks';
import { brandsActions } from '@actions';
import {
    COMPANY_STATUS,
    LocationType,
    MAX_CERTIFICATION_LENGTH,
    MAX_CERTIFICATIONS,
    MAX_STORE_CODE_LENGTH,
} from '@constants/companies';
import { LOCALE } from '@constants/locale';
import { Industry } from '@constants/account';
import { FORM_TYPE } from '@constants/brands';

const CertificationsList = styled('div')({
    gridColumn: '1 / -1',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
});

export const Form = styled('div')({
    maxWidth: 698,
    display: 'grid',
    columnGap: 24,
    rowGap: 16,
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    '@media (max-width: 1024px)': {
        gridTemplateColumns: 'minmax(0, 1fr)',
        maxWidth: 'none',
    },
});

const GeneralInfo = ({ locale, industry }) => {
    const [modal, setModal] = useState({ open: false, type: FORM_TYPE.NEW });

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { control, setValue, watch } = useFormContext();
    const { isSubmitting } = useFormState({ control });
    const type = watch('type');

    const {
        fields: certificationFields,
        append: appendCertification,
        remove: removeCertification,
    } = useFieldArray({ control, name: 'certifications' });

    const brands = useSelector(state => state.brands.data);
    const degrees = useSelector(state => state.account.degrees);
    const { hasFullSettings } = usePermissions();

    const refetchBrandsCallback = () => dispatch(brandsActions.getAll());

    const statusOptions = [
        { value: COMPANY_STATUS.OPEN, label: t('common.open') },
        { value: COMPANY_STATUS.CLOSED, label: t('common.closed') },
    ];

    const typeOptions = [
        { value: LocationType.Location, label: t('common.location') },
        ...(industry === Industry.HealthcareAndDental
            ? [{ value: LocationType.HealthcareFacility, label: t('SettingsCompanies.healthcareFacility') }]
            : []),
        ...(industry === Industry.HealthcareAndDental && locale === LOCALE.UNITED_STATES
            ? [{ value: LocationType.HealthcareProfessional, label: t('SettingsCompanies.healthcareProfessional') }]
            : []),
        ...([Industry.HotelAndLodging, Industry.Restaurant, Industry.Other].includes(industry)
            ? [{ value: LocationType.Hotel, label: t('SettingsCompanies.hotel') }]
            : []),
        ...([Industry.HotelAndLodging, Industry.Restaurant, Industry.FoodAndGrocery, Industry.Other].includes(industry)
            ? [{ value: LocationType.Restaurant, label: t('SettingsCompanies.restaurant') }]
            : []),
    ];

    const brandsOptions = brands.map(brand => ({ value: brand.id, label: brand.title }));
    const degreesOptions = degrees.map(degree => ({ value: degree.id, label: degree.value }));

    return (
        <div>
            <Form>
                <Controller
                    control={control}
                    name="internalName"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('SettingsCompanies.internalName')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="locationName"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('SettingsCompanies.locationName')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="type"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            onChange={({ target: { value } }) => {
                                setValue('type', value, { shouldDirty: true });
                            }}
                            options={typeOptions}
                            label={t('common.type')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="brand"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            ref={ref}
                            inputProps={{
                                ...field,
                                onChange: e => {
                                    if (e.target.value !== 'create-new') {
                                        field.onChange(e.target.value);
                                    }
                                },
                            }}
                            onChange={({ target: { value } }) => {
                                if (value !== 'create-new') {
                                    setValue('brand', value, { shouldDirty: true });
                                }
                            }}
                            options={brandsOptions}
                            label={t('common.brand')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            withEndAdornment={!!field.value}
                            onClickEndAdornment={() => setValue('brand', '', { shouldDirty: true })}
                            creatable={hasFullSettings()}
                            creatableText={t('SettingsBrands.createNewBrand')}
                            creatableClick={() => setModal({ open: true, type: FORM_TYPE.NEW })}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="status"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            onChange={({ target: { value } }) => {
                                setValue('status', value, { shouldDirty: true });
                            }}
                            options={statusOptions}
                            label={t('common.status')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="geoModifier"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('SettingsCompanies.geoModifier')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="storeCode"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('SettingsCompanies.storeCode')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message, { count: MAX_STORE_CODE_LENGTH }) : null}
                        />
                    )}
                />
                {industry === Industry.HealthcareAndDental && type === LocationType.HealthcareProfessional && (
                    <>
                        <Controller
                            control={control}
                            name="firstName"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    label={t('common.firstName')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                    required
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="lastName"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    label={t('common.lastName')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                    required
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="degreeId"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Select
                                    {...field}
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    onChange={({ target: { value } }) => {
                                        setValue('degreeId', value, { shouldDirty: true });
                                    }}
                                    options={degreesOptions}
                                    label={t('SettingsCompanies.degree')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                    required
                                />
                            )}
                        />
                    </>
                )}
                {locale === LOCALE.UNITED_STATES &&
                    (type === LocationType.HealthcareProfessional || type === LocationType.HealthcareFacility) && (
                        <Controller
                            control={control}
                            name="nationalHealthProviderId"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    label={t('SettingsCompanies.nationalHealthProviderId')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                    required={type === LocationType.HealthcareProfessional}
                                />
                            )}
                        />
                    )}
                {industry === Industry.HealthcareAndDental && type === LocationType.HealthcareProfessional && (
                    <CertificationsList>
                        {certificationFields.map((field, index) => (
                            <FieldWithActions
                                key={field.id}
                                actions={rowActions({
                                    index,
                                    count: certificationFields.length,
                                    onAdd: () => appendCertification({ name: '' }),
                                    onDelete: () => removeCertification(index),
                                    canAdd: certificationFields.length < MAX_CERTIFICATIONS,
                                    disabled: isSubmitting,
                                    t,
                                })}
                            >
                                <Controller
                                    control={control}
                                    name={`certifications.${index}.name`}
                                    render={({ field: ctrl, fieldState: { error }, ref }) => (
                                        <Input
                                            ref={ref}
                                            inputProps={{ ...ctrl, onChange: e => ctrl.onChange(e.target.value) }}
                                            label={`${t('SettingsCompanies.certifications')} ${index + 1}`}
                                            fullWidth
                                            error={!!error}
                                            helperText={
                                                error ? t(error.message, { count: MAX_CERTIFICATION_LENGTH }) : null
                                            }
                                        />
                                    )}
                                />
                            </FieldWithActions>
                        ))}
                    </CertificationsList>
                )}
            </Form>
            {modal.open ? (
                <BrandManageModal
                    modal={modal}
                    onClose={() => setModal({ open: false, type: FORM_TYPE.NEW })}
                    refetchCallback={refetchBrandsCallback}
                />
            ) : null}
        </div>
    );
};

export default GeneralInfo;
